import { Navigate, useLocation, useNavigate } from 'react-router';
import { PaymentMethod, language } from '../common/constants';
import { object, number, string } from 'yup';
import { getPageUrl, getQueryParams, safeCast } from '../utils/urlUtils';
import { Alert, Badge, Button, Container, Form, Spinner } from 'react-bootstrap';
import { createVietQRUrl } from '../apis/paymentApi';
import { Loader } from '../controls/Loader';
import { Helmet } from 'react-helmet';
import { StyledBreadcrumb } from './Review.styled';
import IconBack from '../icons/IconBack';
import { useContext, useEffect, useState } from 'react';
import { HotelContext } from '../modules/HotelContext';
import { StyledHotelName } from './Search.styled';
import { getHotelReservation } from '../apis/hotelApis';
import html2canvas from 'html2canvas';
import { useScrollIntoViewIframe } from '../hooks/useScrollIntoView';
import { PaymentStatus } from '../types/hotelTypes';
import { useQuery } from '@tanstack/react-query';
import { navigatorWebShare } from '../utils/iframeUtils';
import { PageAction } from '../types/newrelicTypes';
import IconCopy from '../icons/IconCopy';

export interface IVietQRRouteParams {
  amount: number;
  hotelId: number;
  reservationId: number;
  paymentMethod: PaymentMethod;
  bookingNumber?: number;
}

const schema = object({
  amount: number().required(),
  hotelId: number().required(),
  reservationId: number().required(),
  paymentMethod: string()
    .required()
    .oneOf([
      PaymentMethod.VietQR,
      PaymentMethod.VietQR_Cake_Email,
      PaymentMethod.VietQR_Timo_Email,
      PaymentMethod.VietQR_VietinBank,
      PaymentMethod.SecurityDeposit,
    ]),
  bookingNumber: number().optional(),
});

export const VietQRPayment = () => {
  const { hotel } = useContext(HotelContext);
  const { search } = useLocation();
  const navigate = useNavigate();
  const params = getQueryParams(search);
  const parsedParams = safeCast<IVietQRRouteParams>(params, schema);
  const [imageBlob, setImageBlob] = useState<Blob>();
  const [downloadError, setDownloadError] = useState<Error>();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isDescriptionCopied, setIsDescriptionCopied] = useState(false);

  if (!parsedParams) {
    return <Alert>{language.GENERIC_ERROR}</Alert>;
  }

  const { hotelId, reservationId } = parsedParams;
  const { data, error } = useQuery({
    queryKey: ['createVietQRUrl', search],
    queryFn: () => createVietQRUrl({ ...parsedParams }),
  });
  const {
    data: reservation,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: [`/api/hotels/${hotelId}/reservation/${reservationId}`],
    queryFn: () => getHotelReservation(hotelId, reservationId),
  });

  useScrollIntoViewIframe(Boolean(data));

  useEffect(() => {
    async function createQRImage() {
      if (!imageLoaded || !reservation?.ReservationNumber) {
        return;
      }

      try {
        const canvas = await html2canvas(document.querySelector('#share-area'), {
          ignoreElements: (element) => element.classList.contains('html2canvas-ignore'),
        });
        const blob = await new Promise<Blob>((resolve) => canvas.toBlob((blob) => resolve(blob)));
        setImageBlob(blob);
      } catch (err) {
        setDownloadError(err);
        window.newrelic?.noticeError(new Error(`Error downloading QR Image: ${err?.toString()}`));
      }
    }

    createQRImage();
  }, [imageLoaded, reservation?.ReservationNumber]);

  const goBack = () => {
    navigate(getPageUrl(`/${hotel.hotelId}/reservation/${parsedParams.reservationId}`));
  };

  const saveToPhoto = async () => {
    const payload = {
      title: `${reservationId}_qr.png`,
      text: `${reservationId}_qr.png`,
      files: [
        new File([imageBlob], `${reservationId}_qr.png`, {
          type: imageBlob.type,
        }),
      ],
    };
    let hasError = false;
    try {
      await navigatorWebShare(payload);
    } catch (err) {
      console.error(err);
      hasError = true;
      window.newrelic?.noticeError(new Error(`Failed to save QR Image`, err?.toString()));
    } finally {
      window.newrelic?.addPageAction(PageAction.DownloadQrImage, {
        ...parsedParams,
        error: hasError.toString(),
      });
    }
  };

  const copyText = async (text: string) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  };

  const copyTransferDescription = async () => {
    if (!data?.description) {
      return;
    }

    await copyText(data.description);
    setIsDescriptionCopied(true);
    window.setTimeout(() => setIsDescriptionCopied(false), 2000);
  };

  if (error || downloadError) {
    return <Alert>{language.GENERIC_ERROR}</Alert>;
  }

  const isPaid =
    reservation?.PaymentStatus === PaymentStatus.Paid || reservation?.PaymentStatus === PaymentStatus.Deposit;
  // Navigate to reservation page when payment is confirmed
  if (isPaid) {
    return <Navigate to={getPageUrl(`/${hotelId}/reservation/${reservationId}`)} />;
  }

  return (
    <div style={{ flex: 1, width: '100%', backgroundColor: 'rgb(255, 255, 255)' }}>
      <Helmet>
        <title>{`Thanh toán VietQR - ${hotel.hotelName}`}</title>
      </Helmet>
      <Container fluid>
        <StyledBreadcrumb>
          <div className="left-breadcrumb">
            <Button size="sm" variant="link" onClick={goBack}>
              <IconBack />
            </Button>
            <StyledHotelName>{hotel.hotelName}</StyledHotelName>
          </div>
          <Button size="sm" variant="link" onClick={goBack}>
            Hủy thanh toán
          </Button>
        </StyledBreadcrumb>
        <div className="mb-4" style={{ padding: '0 1rem', textAlign: 'center' }}>
          {!data && <Loader />}
          <div id="share-area" className="pb-2">
            {data && (
              <img
                className="img-fluid"
                style={{ minHeight: '20rem' }}
                src={`data:${data.imageMimeType || 'image/png'};base64,${data.imageBase64}`}
                onLoad={() => setImageLoaded(true)}
              />
            )}
            {data && reservation && (
              <div style={{ fontFamily: 'Arial', color: '#555' }}>
                {data.description && (
                  <div
                    className="mb-2"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      maxWidth: '100%',
                      flexWrap: 'wrap',
                    }}>
                    <span>Nội dung CK: </span>
                    <b style={{ color: '#111', wordBreak: 'break-word' }}>{data.description}</b>
                    <Button
                      className="html2canvas-ignore"
                      size="sm"
                      variant="outline-secondary"
                      onClick={copyTransferDescription}
                      aria-label="Sao chép nội dung chuyển khoản"
                      title="Sao chép nội dung chuyển khoản">
                      <IconCopy />
                    </Button>
                    {isDescriptionCopied && (
                      <span className="html2canvas-ignore" style={{ color: '#198754', fontWeight: 600 }}>
                        Đã sao chép
                      </span>
                    )}
                  </div>
                )}
                <div>
                  <span>Mã đặt phòng: </span>
                  <b>#{reservation.ReservationNumber}</b>
                </div>
                <div>
                  <span>Khách sạn: </span>
                  <b>{hotel.hotelName}</b>
                </div>
                <div>
                  <span>Trạng thái: </span>
                  {isPaid ? (
                    <Badge pill bg="success">
                      Đã thanh toán
                    </Badge>
                  ) : (
                    <Badge pill bg="danger">
                      Chưa thanh toán
                    </Badge>
                  )}
                </div>

                <Alert variant="warning" style={{ padding: '6px', fontWeight: 600, marginTop: '1rem' }}>
                  Lưu ý: Vui lòng không thay đổi nội dung chuyển khoản
                </Alert>
              </div>
            )}
          </div>
          {data && reservation && (
            <>
              <div className="mb-2">
                <div style={{ textAlign: 'left', padding: '0 2rem', margin: 'auto', display: 'inline-block' }}>
                  <div className="mb-1">
                    <b>Hướng dẫn thanh toán bằng ứng dụng ngân hàng</b>
                  </div>
                  <Form.Text>
                    <div>
                      1. Chụp màn hình hoặc{' '}
                      <a
                        href={data?.imageUrl}
                        onClick={(e) => {
                          e.preventDefault();
                          saveToPhoto();
                        }}>
                        lưu mã QR vào Thư viện Ảnh
                      </a>
                    </div>
                    <div>2. Mở ứng dụng ngân hàng, chọn Quét mã QR</div>
                    <div>3. Chọn ảnh QR đã lưu từ Thư viện Ảnh</div>
                    <div>4. Thực hiện thanh toán trên ứng dụng ngân hàng</div>
                  </Form.Text>
                </div>
              </div>
              {isFetching && <Spinner size="sm" />}
              <Button size="sm" variant="link" onClick={() => refetch()}>
                Tải mới thông tin thanh toán
              </Button>
            </>
          )}
        </div>
      </Container>
    </div>
  );
};
